import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Send, Loader2, Trash2, Bot, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message optimistically
    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/chat/send`, { message: userMessage });
      // Replace temp message and add AI response
      setMessages(prev => [
        ...prev.filter(m => m.id !== tempUserMsg.id),
        { ...tempUserMsg, id: `user-${Date.now()}` },
        response.data
      ]);
    } catch (error) {
      toast.error('Errore nell\'invio del messaggio');
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = async () => {
    try {
      await axios.delete(`${API}/chat/clear`);
      setMessages([]);
      toast.success('Cronologia eliminata');
    } catch (error) {
      toast.error('Errore nell\'eliminazione della cronologia');
    }
  };

  const suggestedQuestions = [
    "Qual è la situazione finanziaria attuale?",
    "Analizza il mio bilancio",
    "Come posso migliorare il flusso di cassa?",
    "Mostrami i conti con saldo più alto"
  ];

  return (
    <div className="flex flex-col h-full" data-testid="chat-page">
      {/* Header */}
      <div className="p-4 lg:p-6 border-b border-border bg-card/50 backdrop-blur-sm flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
            <Bot size={20} className="text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-xl font-bold text-foreground">AI Assistant</h1>
          </div>
        </div>
        {messages.length > 0 && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleClearChat}
            className="text-muted-foreground hover:text-destructive"
            data-testid="clear-chat-btn"
          >
            <Trash2 size={16} className="mr-2" />
            Pulisci
          </Button>
        )}
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4 lg:p-6">
        {historyLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col h-full">
            <div className="space-y-4 max-w-4xl mx-auto flex-1">
              {/* Initial AI message */}
              <div className="flex gap-3 justify-start animate-fade-in">
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                  <Bot size={16} className="text-primary" />
                </div>
                <div className="chat-message-ai">
                  <p className="text-base font-bold text-foreground mb-3">
                    Ciao! Sono IAnus, il tuo assistente AI per la gestione del budget aziendale
                  </p>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                    Sono a tua disposizione per supportarti in tutte le attività di cui necessiti. Puoi chiedermi infatti analisi, consigli o informazioni sui tuoi dati finanziari oppure possiamo limitarci ad inserire tutti i dati utili per iniziare ad elaborare il primo prospetto.
                  </p>
                </div>
              </div>
            </div>
            {/* Suggested questions */}
            <div className="max-w-4xl mx-auto w-full mt-6">
              <p className="text-sm text-muted-foreground/60 mb-3">Puoi chiedermi ad esempio:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInput(q);
                      inputRef.current?.focus();
                    }}
                    className="p-3 text-left text-sm rounded-xl bg-card border border-border hover:border-primary/50 transition-colors"
                    data-testid={`suggested-question-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-w-4xl mx-auto">
            {messages.map((msg, index) => (
              <div
                key={msg.id}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
                data-testid={`chat-message-${index}`}
              >
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                    <Bot size={16} className="text-primary" />
                  </div>
                )}
                <div
                  className={msg.role === 'user' ? 'chat-message-user' : 'chat-message-ai'}
                >
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0">
                    <User size={16} className="text-primary-foreground" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3 justify-start animate-fade-in">
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                  <Bot size={16} className="text-primary" />
                </div>
                <div className="chat-message-ai">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Input Area */}
      <div className="p-4 lg:p-6 border-t border-border bg-card/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-background border-border p-2">
            <div className="flex items-center gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Scrivi un messaggio..."
                className="flex-1 border-0 bg-transparent focus-visible:ring-0 h-12"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className="h-10 w-10 p-0 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl"
                data-testid="send-btn"
              >
                {loading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
